import { createSlice, isAnyOf, type PayloadAction } from '@reduxjs/toolkit';

import { shopApi } from '@/api/queries/shop';
import { type GoodsType } from '@/common/types/goods';
import { type TicketRewardType } from '@/common/types/lottery';
import { type PlantTypeText } from '@/common/types/plant';
import { type ResourceTypeText } from '@/common/types/resource';
import {
  type ActivatePromoCodeResponseType,
  type AllShopInfo,
  type ArenaOffersDto,
  type ArenaPackRewardDto,
  type BoxList,
  type GetArenaOffersRequestType,
} from '@/common/types/shop/marketBoxes';
import { type PromoCodeRewardState } from '@/common/types/shop/promo';

const initialState: AllShopInfo = {
  shopPacks: [],
  arenaOffers: [],
  profitDropOffers: [],
  mustShowArenaPopup: false,
  activeProfitDropOffer: null,
  bingoEvent: null,
  promoCode: {
    activeRewardIndex: null,
    rewards: [],
  },
};

const shopSlice = createSlice({
  name: 'shop',
  initialState,
  reducers: {
    setClaimedReward: (state, { payload }: PayloadAction<number>) => {
      if (!state.activeProfitDropOffer) return;
      state.activeProfitDropOffer.claimRewardIndex = payload;
    },
    changeProfitDropOffer: (
      state,
      { payload }: PayloadAction<ArenaOffersDto | null>,
    ) => {
      state.activeProfitDropOffer = payload;
    },
    showPopup: (state, { payload }: PayloadAction<boolean>) => {
      state.mustShowArenaPopup = payload;
    },
    discountTimer: (state) => {
      const packs = state.arenaOffers;

      if (packs.length) {
        for (const pack of packs) {
          if (pack.discountTimeLeftInSeconds > 0) {
            pack.discountTimeLeftInSeconds = pack.discountTimeLeftInSeconds - 1;
          }
        }
      }

      if (state.bingoEvent) {
        if (state.bingoEvent.endsInSeconds > 0) {
          state.bingoEvent.endsInSeconds -= 1;
        }

        if (
          state.bingoEvent.phase === 'ratingWindow' &&
          state.bingoEvent.ratingClosesInSeconds !== null &&
          state.bingoEvent.ratingClosesInSeconds > 0
        ) {
          state.bingoEvent.ratingClosesInSeconds -= 1;
        }
      }
    },
    fixResourceArenaOffer: (
      state,
      { payload }: PayloadAction<{ id: string; order: number }>,
    ) => {
      if (!state.arenaOffers.length && !state.profitDropOffers.length) return;

      let existingOffer = state.arenaOffers.find((el) => el.id === payload.id);

      if (!existingOffer && payload.id === state.activeProfitDropOffer?.id) {
        existingOffer = state.activeProfitDropOffer;
      }

      if (!existingOffer) return;

      let offerReward;

      if (
        ['profit_drop_small', 'profit_drop_large'].includes(existingOffer.type)
      ) {
        offerReward = existingOffer.reward.arenaOffer || null;
      } else {
        offerReward = existingOffer.reward;
      }

      if (!offerReward) return;

      // Спочатку знаходимо обраний елемент
      let foundItem = null;
      for (const category of ['resources', 'materials', 'products'] as const) {
        const items = offerReward[category];

        if (items && Array.isArray(items)) {
          foundItem = items.find((item) => {
            const [, data] = Object.entries(item)[0];

            return data.order === payload.order;
          });

          if (foundItem) break;
        }
      }

      // Якщо елемент не знайдено - виходимо
      if (!foundItem) return;

      const [, foundData] = Object.entries(foundItem)[0];

      // Перевіряємо, чи вже є fixed: true.
      // Якщо є, то знімаємо його з УСІХ, включаючи поточний.
      // Якщо немає, то знімаємо з УСІХ, і встановлюємо для поточного.
      const isCurrentlyFixed = foundData.fixed;

      // Зняти fixed з усіх, щоб залишився лише один або жодного
      for (const category of ['resources', 'materials', 'products'] as const) {
        const items = offerReward[category];

        if (items && Array.isArray(items)) {
          for (const item of items) {
            const [, data] = Object.entries(item)[0];

            if ('fixed' in data) {
              data.fixed = false;
            }
          }
        }
      }

      // Встановлюємо fixed для обраного елемента, якщо він не був зафіксований раніше
      if (!isCurrentlyFixed) {
        foundData.fixed = true;
      }
    },
    toNextPromoReward: (state) => {
      const rewardsCount = state.promoCode.rewards.length;

      if (state.promoCode.activeRewardIndex !== null) {
        const index = state.promoCode.activeRewardIndex;

        if (index < rewardsCount - 1) {
          state.promoCode.activeRewardIndex += 1;
        } else {
          state.promoCode.activeRewardIndex = null;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      shopApi.endpoints.getShopInfo.matchFulfilled,
      (state, { payload }: PayloadAction<BoxList[]>) => {
        state.shopPacks = payload;
      },
    );
    builder.addMatcher(
      shopApi.endpoints.getArenaOffers.matchFulfilled,
      (state, { payload }: PayloadAction<GetArenaOffersRequestType>) => {
        const { profitDropOffers, otherOffers } = payload.offers.reduce(
          (acc, offer) => {
            const isProfitDrop =
              offer.type === 'profit_drop_small' ||
              offer.type === 'profit_drop_large';

            if (isProfitDrop) {
              acc.profitDropOffers.push(offer);
            } else {
              acc.otherOffers.push(offer);
            }

            return acc;
          },
          {
            profitDropOffers: [],
            otherOffers: [],
          } as {
            profitDropOffers: ArenaOffersDto[];
            otherOffers: ArenaOffersDto[];
          },
        );
        state.arenaOffers = otherOffers;
        state.profitDropOffers = profitDropOffers;

        if (state.profitDropOffers.length) {
          const notCompletedOffer = state.profitDropOffers.find(
            (el) => el.isBuyingProcess,
          );
          state.activeProfitDropOffer = notCompletedOffer
            ? notCompletedOffer
            : state.profitDropOffers[0];
        }

        state.bingoEvent = payload.bingoEvent ?? null;
      },
    );
    builder.addMatcher(
      isAnyOf(
        shopApi.endpoints.shopBuy.matchFulfilled,
        shopApi.endpoints.shopShuffle.matchFulfilled,
      ),
      (state, { payload }: PayloadAction<BoxList>) => {
        if (state.shopPacks) {
          // Шукаємо індекс елемента з таким же name, як у payload
          const existingBoxIndex = state.shopPacks.findIndex(
            (box) => box.name === payload.name,
          );

          if (existingBoxIndex !== -1) {
            // Якщо елемент із таким name знайдено, перезаписуємо його
            state.shopPacks[existingBoxIndex] = payload;
          }
        }
      },
    );
    builder.addMatcher(
      shopApi.endpoints.shuffleArenaOffer.matchFulfilled,
      (
        state,
        { payload }: PayloadAction<{ id: string; reward: ArenaPackRewardDto }>,
      ) => {
        if (state.arenaOffers.length || state.profitDropOffers.length) {
          const existingOfferIndex = state.arenaOffers.findIndex(
            (el) => el.id === payload.id,
          );

          if (existingOfferIndex !== -1) {
            state.arenaOffers[existingOfferIndex].reward = payload.reward;

            return;
          }

          const existingProfitOfferIndex = state.profitDropOffers.findIndex(
            (el) => el.id === payload.id,
          );

          if (existingProfitOfferIndex !== -1) {
            state.profitDropOffers[existingProfitOfferIndex].reward =
              payload.reward;

            if (payload.id === state.activeProfitDropOffer?.id) {
              state.activeProfitDropOffer.reward = payload.reward;
            }

            return;
          }
        }
      },
    );
    builder.addMatcher(shopApi.endpoints.showPoput.matchFulfilled, (state) => {
      if (state.arenaOffers.length) {
        state.arenaOffers = state.arenaOffers.map((el) => ({
          ...el,
          popupShown: true,
        }));
      }
    });
    builder.addMatcher(
      shopApi.endpoints.activatePromoCode.matchFulfilled,
      (state, { payload }: PayloadAction<ActivatePromoCodeResponseType>) => {
        let result: PromoCodeRewardState[] = [];

        const walletRewards = payload.rewards.wallet;
        const materialsRewards = payload.rewards.materials;
        const lotteryRewards = payload.rewards.lottery;

        if (walletRewards) {
          const walletRewardsArray: PromoCodeRewardState[] = Object.entries(
            walletRewards,
          ).map(([item, value]) => ({
            kind: 'wallet',
            item: item as 'cash' | 'tgem' | 'usdt' | 'dice',
            value: value || 0,
          }));

          result = result.concat(walletRewardsArray);
        }

        if (materialsRewards) {
          const materialsRewardsArray: PromoCodeRewardState[] = Object.entries(
            materialsRewards,
          ).map(([item, value]) => ({
            kind: 'materials',
            item: item as ResourceTypeText | PlantTypeText | GoodsType,
            value,
          }));

          result = result.concat(materialsRewardsArray);
        }

        if (payload.rewards.vip) {
          result = result.concat({
            kind: 'vip',
            item: 'vip',
            value: payload.rewards.vip,
          });
        }

        if (lotteryRewards) {
          const rewards = Object.entries(lotteryRewards).map(
            ([item, value]) => ({
              kind: 'lottery' as const,
              item: item as TicketRewardType,
              value,
            }),
          );

          result = result.concat(rewards);
        }

        state.promoCode.rewards = result;
        state.promoCode.activeRewardIndex = 0;
      },
    );
  },
});

export default shopSlice.reducer;
export const {
  setClaimedReward,
  discountTimer,
  fixResourceArenaOffer,
  showPopup,
  toNextPromoReward,
  changeProfitDropOffer,
} = shopSlice.actions;
